/*
 * Copyright © 2018-2023; GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced; copied; distributed; disseminated to the public; adapted or transmitted
 * in any form or by any means; including photocopying; recording; or other electronic or
 * mechanical methods; without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement } from "react";
import { IconProps } from "./IconProps";

export enum SpinnerSize {
  ExtraSmall,
  Small,
  Medium,
  Large,
  ExtraLarge,
  Huge,
  VideoChat,
}

interface Props {
  dark?: boolean;
  size?: SpinnerSize;
  thick?: boolean;
}

export const Spinner = ({
  className = "",
  dark = false,
  size = SpinnerSize.Medium,
  thick = true,
}: IconProps & Props): ReactElement => {
  const baseClassName = "inline animate-spin text-white";

  let sizeClassName;
  switch (size) {
    case SpinnerSize.ExtraSmall:
      sizeClassName = "w-2 h-2";
      break;
    case SpinnerSize.Small:
      sizeClassName = "w-3 h-3";
      break;
    case SpinnerSize.Medium:
      sizeClassName = "w-4 h-4";
      break;
    case SpinnerSize.Large:
      sizeClassName = "w-5 h-5";
      break;
    case SpinnerSize.ExtraLarge:
      sizeClassName = "w-6 h-6";
      break;
    case SpinnerSize.Huge:
      sizeClassName = "w-8 h-8";
      break;
    case SpinnerSize.VideoChat:
      sizeClassName = "w-20 h-20";
      break;
    default:
      sizeClassName = "w-4 h-4";
  }

  if (thick) {
    return (
      <svg
        className={`${baseClassName} ${sizeClassName} ${className}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className={dark ? "opacity-50" : "opacity-25"}
          cx="12"
          cy="12"
          r="10"
          stroke={dark ? "#1e293b" : "currentColor"}
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      role="status"
      className={`${baseClassName} ${sizeClassName} ${className}`}
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      strokeWidth={10}
    >
      <path
        className={dark ? "opacity-100" : "opacity-50"}
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="#E5E7EB"
      />
      <path
        className={dark ? "opacity-50" : "opacity-100"}
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill={dark ? "#1e293b" : "currentColor"}
      />
    </svg>
  );
};
